import { type AppRouter } from '@bibeltv-leseplaene/api'
import { createTRPCVueQueryClient } from '@falcondev-oss/trpc-vue-query'
import { useQueryClient } from '@tanstack/vue-query'
import { TRPCClientError, type TRPCLink, httpSubscriptionLink, splitLink } from '@trpc/client'
import { observable } from '@trpc/server/observable'
import superjson from 'superjson'
import { httpLink } from 'trpc-nuxt/client'

function getAuthToken() {
  const store = useKeycloak()
  const mbtv = useMBTV()
  return {
    authorization: store.isAuthenticated
      ? `Keycloak ${store.keycloak.token}`
      : mbtv.isAuthenticated
        ? `Bearer ${mbtv.mbtv.token ?? ''}`
        : '',
  }
}

function authLink(): TRPCLink<AppRouter> {
  let isRefreshing = false
  let refreshPromise: Promise<void> | null = null
  let pendingRequests: (() => void)[] = []

  async function refreshAll() {
    const keycloak = useKeycloak()
    const mbtv = useMBTV()
    const auth = useAuth()

    await keycloak.updateToken()
    await mbtv.updateToken()

    if (!auth.isAuthenticated) {
      throw new Error('Not authenticated')
    }

    try {
      await auth.refresh()
    }
    catch (err) {
      throw new Error('Auth refresh failed')
    }

    await auth.waitForAuth()

    if (auth.session.status !== 'authenticated') {
      throw new Error('Auth failed')
    }
  }

  async function refreshToken() {
    if (!refreshPromise) {
      refreshPromise = refreshAll()
        .then(() => {
          refreshPromise = null
        })
        .catch((err) => {
          refreshPromise = null
          throw err
        })
    }

    return refreshPromise
  }

  function resolvePending() {
    for (const cb of pendingRequests) {
      cb()
    }
    pendingRequests = []
  }

  async function logout() {
    const keycloak = useKeycloak()
    const mbtv = useMBTV()

    keycloak.logout(true)
    mbtv.logout()
  }

  return () =>
    ({ next, op }) => {
      return observable((observer) => {
        const execute = () => {
          return next(op).subscribe({
            next(value) {
              observer.next(value)
            },
            async error(err) {
              if (err.message === '{"message":"Unauthorized"}' || err.data?.httpStatus === 401) {
                if (isRefreshing) {
                  pendingRequests.push(() => execute())
                  return
                }

                try {
                  isRefreshing = true
                  await refreshToken()
                  isRefreshing = false

                  resolvePending()
                  execute()
                }
                catch {
                  isRefreshing = false
                  pendingRequests = []
                  await logout()
                }
              }

              if (err.data?.httpStatus === 403) {
                const toast = useToast()
                toast.add({ title: 'Es fehlen benötigte Berechtigungen', color: 'red' })

                await logout()
              }

              observer.error(err)
            },
          })
        }

        return execute()
      })
    }
}

export default defineNuxtPlugin({
  dependsOn: ['vue-query'],
  name: 'trpc',
  setup: () => {
    const queryClient = useQueryClient()
    queryClient.setDefaultOptions({ queries: { retry: false } })
    const config = useRuntimeConfig()

    const trpc = createTRPCVueQueryClient<AppRouter>({
      queryClient,
      trpc: {
        links: [
          authLink(),
          splitLink({
            condition: (op) => op.type === 'subscription',
            true: httpSubscriptionLink({
              url: `${config.public.apiBaseUrl}/trpc`,
              transformer: superjson,
            }),
            false: httpLink({
              headers: () => {
                return getAuthToken()
              },
              url: `${config.public.apiBaseUrl}/trpc`,
              transformer: superjson,
              fetch: async (input, init) => {
                const res = await fetch(input, {
                  ...init,
                  credentials: 'include',
                })

                if (!res.ok) {
                  if (input.toString().includes('auth.me')) {
                    const keycloak = useKeycloak()
                    const mbtv = useMBTV()

                    keycloak.logout()
                    mbtv.logout()
                  }
                  else if (res.status === 401) {
                    const text = await res.text()
                    throw new TRPCClientError(text)
                  }
                }

                return res
              },
            }),
          }),
        ],
      },
    })
    return {
      provide: {
        trpc,
      },
    }
  },
})
